/**
 * PPS Request Builder
 *
 * Pure function: checkout state → GetExtrasRequest.
 * Encapsulates all PPS-specific data mapping:
 * - ISO dates → /Date(ms)/ .NET format
 * - nightCount → Durations[] array
 * - productCode (GIATA code) → PackageBookingCode
 * - adultCount from passengers
 */
import { PPS_CONFIG } from '@/services/api/pps/config';
import { toUtcMidnight } from '@/utils/formatting/dateFormatter';
import type { GetExtrasRequest } from '@/types/schemas/pps';

export interface PpsRequestParams {
  catalogCode: string;
  productCode: string;
  checkIn: Date;
  checkOut: Date;
  nightCount: number;
  adultCount: number;
}

/**
 * Calendar date → /Date(ms)/ anchored at UTC midnight. checkIn/checkOut arrive as
 * local-midnight Dates (from the checkout URL parse); raw getTime() would land on the
 * previous day for CET/CEST (VAM-258 bug class).
 */
function toNetDate(date: Date): string {
  return `/Date(${toUtcMidnight(date).getTime()})/`;
}

/**
 * Build the complete GetPackagePricesAndAvailabilitiesV2 request from checkout state.
 */
export function buildPpsExtrasRequest(params: PpsRequestParams): GetExtrasRequest {
  const { catalogCode, productCode, checkIn, checkOut, nightCount, adultCount } = params;

  return {
    Login: {},
    SearchCriteria: {
      CatalogCode: catalogCode,
      Agency: PPS_CONFIG.AGENCY_NUMBER,
      Durations: [nightCount],
      ManualSchedules: {
        Schedules: [
          {
            From: toNetDate(checkIn),
            To: toNetDate(checkOut),
          },
        ],
      },
    },
    PackageSearchCriteria: [
      {
        PackageBookingCode: productCode,
        Adults: adultCount,
        ComponentsToInclude: 1,
        IgnorePackageServiceTypes: true,
        UsePackageDatesForVariableServices: true,
        IgnoreMaxOccupancyForAccommodations: true,
      },
    ],
    ShowNotAvailable: true,
  };
}
